/**
 * Class Variance Authority (CVA) utility for creating type-safe component variants
 * This replaces the need for external CVA library with a lightweight implementation
 */

import { VariantConfig, ClassInput, CompoundVariant } from '../models/class-variance.model';

export { VariantConfig, ClassValue, ClassArray, ClassDictionary, ClassInput } from '../models/class-variance.model';

/**
 * Utility function to combine class names (like clsx/classnames)
 */
export function cn(...inputs: ClassInput[]): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === 'string' || typeof input === 'number') {
      classes.push(String(input));
    } else if (Array.isArray(input)) {
      const result = cn(...input);
      if (result) classes.push(result);
    } else if (typeof input === 'object') {
      for (const key in input) {
        if (input[key]) classes.push(key);
      }
    }
  }

  return classes.join(' ');
}

export function cva<T extends Record<string, Record<string, string>>>(config: VariantConfig<T>) {
  return function (props: { [K in keyof T]?: keyof T[K] } = {}): string {
    const { base = '', variants = {} as T, defaultVariants = {} } = config;

    let result = base;

    // Type-safe iteration over variant keys
    const variantKeys = Object.keys(variants) as Array<keyof T>;

    for (const variantKey of variantKeys) {
      const variantValue = props[variantKey] ?? (defaultVariants as Record<string, unknown>)[variantKey as string];
      if (variantValue && variants[variantKey] && variants[variantKey][variantValue as string]) {
        result = cn(result, variants[variantKey][variantValue as string]);
      }
    }

    // Handle compoundVariants if provided in the config.
    // A compoundVariant entry looks like: { position: ['left','right'], size: 'sm', class: 'w-64' }
    const compoundVariants = config.compoundVariants as Array<CompoundVariant<T>> | undefined;
    if (Array.isArray(compoundVariants)) {
      for (const cv of compoundVariants) {
        const { class: cvClass, ...conditions } = cv as CompoundVariant<T>;
        let match = true;
        const conditionsRecord = conditions as Record<string, unknown>;
        for (const condKey of Object.keys(conditionsRecord) as Array<keyof T>) {
          const condVal = conditionsRecord[condKey as string] as string | Array<string> | undefined;
          const propVal =
            (props as Record<string, unknown>)[condKey as string] ??
            (defaultVariants as Record<string, unknown>)[condKey as string];

          if (Array.isArray(condVal)) {
            if (!condVal.includes(String(propVal))) {
              match = false;
              break;
            }
          } else {
            if (String(propVal) !== String(condVal)) {
              match = false;
              break;
            }
          }
        }
        if (match && cvClass) {
          result = cn(result, cvClass);
        }
      }
    }

    return result;
  };
}

/**
 * Example usage:
 *
 * const buttonVariants = cva({
 *   base: "inline-flex items-center justify-center rounded-md font-medium transition-colors",
 *   variants: {
 *     variant: {
 *       default: "bg-primary text-white hover:bg-primary/90",
 *       secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
 *     },
 *     size: {
 *       default: "h-10 px-4 py-2",
 *       sm: "h-9 rounded-md px-3",
 *       lg: "h-11 rounded-md px-8",
 *     },
 *   },
 *   defaultVariants: {
 *     variant: "default",
 *     size: "default",
 *   },
 * });
 *
 * // Usage: buttonVariants({ variant: "secondary", size: "sm" })
 */
